import { Link } from "react-router-dom";
import {
  ArrowRight, Atom, BarChart3, Bookmark, BookOpen, ChevronRight, Clock, Coins,
  Cpu, Landmark, Lock, Network, Rocket, Share2, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Article } from "@/lib/types";
import api, { timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TxTitle, useArticlePath, useCardTx } from "@/lib/i18n";
import { homeTrack, SectionHead } from "./shared";
import { InfoTip } from "./regmap/regShared";
import { useAdRotation, AdLink, AdMedia } from "@/components/AdSlot";

type RA = Article & { bucket?: string | null };
interface ResearchPayload {
  featured: RA | null;
  cards?: RA[];
  pulse?: { items: RA[]; live: boolean };
  explainers?: Article[];
}

const V = "#c24dff", C = "#05d1e5", B = "#0f52ff";
const SURF = "bg-[#020306] border border-[#1c2436]";
const RANK_ACCENTS = [V, C, B, V];
const EXPL_ACCENTS = [B, C, B, V, C];

const BUCKET_META: Record<string, { label: string; accent: string; art: string }> = {
  markets: { label: "MARKETS", accent: V, art: "/research/cat-markets.jpg" },
  defi: { label: "DEFI", accent: C, art: "/research/cat-defi.jpg" },
  regulation: { label: "REGULATION", accent: B, art: "/research/cat-regulation.jpg" },
  onchain: { label: "ON-CHAIN", accent: V, art: "/research/cat-onchain.jpg" },
};
const CAT_TO_BUCKET: Record<string, string> = {
  markets: "markets", defi: "defi", "government-policy": "regulation",
  blockchain: "onchain", web3: "onchain",
};
const CAT_LABELS: Record<string, string> = {
  markets: "MARKETS", crypto: "CRYPTO", defi: "DEFI", "government-policy": "REGULATION",
  blockchain: "ON-CHAIN", web3: "WEB3", research: "RESEARCH", explainers: "EXPLAINER",
  ai: "AI", quantum: "QUANTUM",
};
const BUCKET_KEYS = ["markets", "defi", "regulation", "onchain"];

const cardMeta = (a: RA, idx: number) => {
  if (a.bucket && BUCKET_META[a.bucket]) return BUCKET_META[a.bucket];
  const mapped = (a.categories || []).find((c) => CAT_TO_BUCKET[c]);
  if (mapped) return { ...BUCKET_META[CAT_TO_BUCKET[mapped]], label: CAT_LABELS[mapped] || BUCKET_META[CAT_TO_BUCKET[mapped]].label };
  const label = CAT_LABELS[(a.categories || [])[0] || ""] || (a.research_label || "Research").toUpperCase();
  return { label, accent: V, art: BUCKET_META[BUCKET_KEYS[idx % 4]].art };
};

const catIcon = (a: Article) => {
  const cats = a.categories || [];
  if (cats.includes("defi")) return Rocket;
  if (cats.includes("government-policy")) return Landmark;
  if (cats.includes("blockchain") || cats.includes("web3")) return Network;
  if (cats.includes("quantum")) return Atom;
  if (cats.includes("ai")) return Cpu;
  if (cats.includes("markets")) return TrendingUp;
  if (cats.includes("explainers")) return BookOpen;
  if (cats.includes("crypto")) return Coins;
  return BarChart3;
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
const authorOf = (a: Article) => a.author_name || a.source_name || null;
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c24dff]/60";

export const FeaturedAnalysis = ({ a, badge = "FEATURED ANALYSIS", ctaLabel = "Read Full Analysis" }:
  { a: RA; badge?: string; ctaLabel?: string }) => {
  const toArticle = useArticlePath();
  const tx = useCardTx(a.slug);
  const { user, refresh } = useAuth();
  const saved = !!user?.saved_articles?.includes(a.id);
  const art = a.lead_visual?.url || a.hero_image_url || "/research/featured-fallback.jpg";
  const title = tx?.title || a.title;

  const onSave = async (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error("Sign in to save research to your account"); return; }
    try {
      if (saved) await api.delete(`/account/saved/${a.id}`);
      else await api.post(`/account/saved/${a.id}`);
      await refresh();
      toast.success(saved ? "Removed from saved" : "Saved to your account");
    } catch { toast.error("Couldn't update saved articles"); }
  };
  const onShare = async (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}${toArticle(a.slug)}`;
    if (navigator.share) { try { await navigator.share({ title: a.title, url }); return; } catch { /* cancelled */ } }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { /* no-op */ }
  };

  return (
    <article data-testid="research-featured" className={`relative ${SURF} rounded-[10px] overflow-hidden lg:flex`}>
      <Link to={toArticle(a.slug)} onClick={() => homeTrack("research", a.slug)} aria-label={a.title}
        data-testid="research-featured-link" className={`absolute inset-0 z-[1] ${FOCUS}`} />
      <div aria-hidden className="absolute inset-x-0 top-0 h-44 sm:h-52 lg:inset-y-0 lg:left-auto lg:right-0 lg:h-full lg:w-[46%] pointer-events-none">
        <img src={art} alt="" loading="lazy" className="w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).src = "/research/featured-fallback.jpg"; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020306] via-[#020306]/25 to-transparent lg:bg-gradient-to-r lg:from-[#020306] lg:via-[#020306]/25 lg:to-transparent" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(70% 80% at 62% 42%, rgba(140,31,255,0.10), transparent 70%)" }} />
      </div>
      <div className="relative z-[2] pointer-events-none flex flex-col p-5 sm:p-6 pt-44 sm:pt-52 lg:pt-6 lg:w-[57%] lg:min-h-[304px] lg:self-stretch">
        <p className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-[#c24dff]/50 bg-[#030407] px-2.5 py-1 nl-mono text-[11px] font-medium text-[#c24dff]">
            {badge}
            {a.access_level === "premium" && <Lock className="h-2.5 w-2.5" />}
          </span>
        </p>        <h3 className="nl-sg font-medium text-[22px] sm:text-[25px] lg:text-[28px] leading-[1.3] text-[#f5f7ff] mt-3.5 line-clamp-3">{title}</h3>
        {a.display_dek && (
          <p className="nl-geist text-[13px] leading-[1.35] text-[#a1abbd] mt-3 line-clamp-2 max-w-[460px]">{a.display_dek}</p>
        )}
        <p className="nl-geist text-[12px] text-[#a1abbd] mt-auto pt-4 flex items-center flex-wrap gap-x-2 gap-y-1" data-testid="research-featured-meta">
          {authorOf(a) && (
            <>
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[#c24dff]" />
              <span>{authorOf(a)}</span>
              <span aria-hidden className="text-[#4a5163] mx-1">•</span>
            </>
          )}
          {fmtDate(a.published_at) && <span>{fmtDate(a.published_at)}</span>}
          {a.read_minutes ? (
            <>
              <span aria-hidden className="text-[#4a5163] mx-1">•</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{a.read_minutes} min read</span>
            </>
          ) : null}
        </p>
        <div className="pointer-events-auto flex items-center gap-2.5 mt-4">
          <Link to={toArticle(a.slug)} data-testid="research-featured-cta" onClick={() => homeTrack("research", a.slug)}
            className={`inline-flex items-center gap-2 rounded-[7px] border border-[#c24dff]/80 bg-[#1c0842] px-4 h-[34px] nl-geist text-[12px] font-medium text-[#f5f7ff] transition-colors hover:bg-[#2a0c5e] ${FOCUS}`}>
            {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button type="button" onClick={onSave} data-testid="research-save-btn"
            aria-label={saved ? "Remove from saved" : "Save this research"}
            className={`h-[34px] w-[34px] rounded-full border border-[#1c2436] bg-[#030406] flex items-center justify-center transition-colors hover:border-[#c24dff]/60 ${FOCUS}`}>
            <Bookmark className={`h-4 w-4 ${saved ? "text-[#c24dff] fill-[#c24dff]" : "text-[#f5f7ff]"}`} />
          </button>
          <button type="button" onClick={onShare} data-testid="research-share-btn" aria-label="Share this research"
            className={`h-[34px] w-[34px] rounded-full border border-[#1c2436] bg-[#030406] flex items-center justify-center text-[#f5f7ff] transition-colors hover:border-[#c24dff]/60 ${FOCUS}`}>
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </article>
  );
};

const ResearchPulse = ({ pulse }: { pulse: { items: RA[]; live: boolean } }) => {
  const toArticle = useArticlePath();
  const items = (pulse.items || []).slice(0, 4);
  const newest = items.reduce((m, a) => ((a.published_at || "") > m ? (a.published_at as string) : m), "");
  return (
    <aside data-testid="research-pulse" className={`${SURF} rounded-[10px] flex flex-col py-4`}>
      <div className="flex items-center gap-2 px-4 sm:px-[18px]">
        <h3 className="nl-sg font-medium text-[14px] tracking-[0.02em] text-[#f5f7ff]">RESEARCH PULSE</h3>
        <InfoTip label="Research Pulse methodology">
          Qualifying NewsLayer research and analysis from the last 7 days, ranked by verified reader engagement.
        </InfoTip>
        <span className="ml-auto" data-testid="research-pulse-status">
          {pulse.live ? (
            <span className="nl-geist text-[12px] font-medium text-[#14db6e]">● LIVE</span>
          ) : (
            <span className="nl-mono text-[10px] uppercase tracking-wide text-[#a1abbd]">Updated {timeAgo(newest)}</span>
          )}
        </span>
      </div>
      <div className="mt-2 flex-1 flex flex-col justify-center">
        {items.map((a, i) => {
          const Icon = catIcon(a);
          const acc = RANK_ACCENTS[i % 4];
          return (
            <div key={a.slug} className={i > 0 ? "border-t border-[#1c2436]/80" : ""}>
              <Link to={toArticle(a.slug)} data-testid={`research-pulse-row-${i + 1}`}
                onClick={() => homeTrack("research", a.slug)}
                className={`group grid grid-cols-[30px_minmax(0,1fr)_auto_auto] items-center gap-x-2.5 px-4 sm:px-[18px] py-2.5 min-h-[56px] transition-colors hover:bg-white/[0.03] ${FOCUS}`}>
                <span aria-hidden className="h-[30px] w-[30px] rounded-md bg-[#030508] border flex items-center justify-center transition-colors"
                  style={{ borderColor: `${acc}66` }}>
                  <Icon className="h-3.5 w-3.5" style={{ color: acc }} />
                </span>
                <span className="min-w-0">
                  <span className="nl-geist text-[12.5px] font-medium leading-[1.3] text-[#f5f7ff] line-clamp-2 group-hover:text-white transition-colors">
                    <TxTitle slug={a.slug} title={a.title} />
                  </span>
                  {a.display_dek && (
                    <span className="nl-geist text-[11px] leading-[1.3] text-[#a1abbd] line-clamp-1 mt-0.5">{a.display_dek}</span>
                  )}
                </span>
                <span className="nl-mono text-[10px] text-[#a1abbd] whitespace-nowrap pl-2">{timeAgo(a.published_at)}</span>
                <ChevronRight className="h-4 w-4 text-[#a1abbd] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-white" />
              </Link>
            </div>
          );
        })}
      </div>
      <div className="px-4 sm:px-[18px] pt-2.5 text-center">
        <Link to="/research" data-testid="research-pulse-viewall" onClick={() => homeTrack("research", "pulse-viewall")}
          className={`nl-geist text-[11px] font-medium text-[#c24dff] hover:text-white transition-colors inline-flex items-center gap-1.5 ${FOCUS}`}>
          View all trending analysis <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </aside>
  );
};

export const ResearchCard = ({ a, idx }: { a: RA; idx: number }) => {
  const toArticle = useArticlePath();
  const meta = cardMeta(a, idx);
  const art = a.lead_visual?.url || a.hero_image_url || meta.art;
  return (
    <Link to={toArticle(a.slug)} data-testid={`research-card-${a.bucket || `slot-${idx}`}`}
      onClick={() => homeTrack("research", a.slug)}
      className={`group ${SURF} rounded-[9px] overflow-hidden flex flex-col transition-colors hover:border-[#31405f] ${FOCUS}`}>
      <span className="relative block h-[104px] sm:h-[112px] xl:h-[92px] overflow-hidden">
        <img src={art} alt="" loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).src = meta.art; }}
          className="w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 group-hover:scale-[1.03]" />
        <span aria-hidden className="absolute inset-0"
          style={{ background: `linear-gradient(to top, #020306 0%, rgba(2,3,6,0.12) 45%, rgba(2,3,6,0.32) 100%), radial-gradient(85% 95% at 50% 15%, ${meta.accent}12, transparent 70%)` }} />
        <span className="absolute top-3.5 left-4 inline-flex rounded-md border bg-[#030407]/90 px-2.5 py-[3px] nl-mono text-[11px] font-medium"
          style={{ borderColor: `${meta.accent}80`, color: meta.accent }}>{meta.label}</span>
      </span>
      <span className="flex flex-col flex-1 p-4">
        <span className="nl-sg font-medium text-[17px] xl:text-[18px] leading-[1.3] text-[#f5f7ff] line-clamp-2 group-hover:text-white transition-colors">
          <TxTitle slug={a.slug} title={a.title} />
        </span>
        {a.display_dek && (
          <span className="nl-geist text-[12px] leading-[1.3] text-[#a1abbd] line-clamp-2 mt-2">{a.display_dek}</span>
        )}
        <span className="mt-auto pt-3.5 flex items-end justify-between gap-3">
          <span className="nl-geist text-[10.5px] leading-[1.35] text-[#a1abbd] min-w-0">
            {authorOf(a) && <>{authorOf(a)}<span aria-hidden className="mx-1.5 text-[#4a5163]">•</span></>}
            {fmtDate(a.published_at)}
            {a.read_minutes ? (
              <><span aria-hidden className="mx-1.5 text-[#4a5163]">•</span>
                <Clock className="inline h-2.5 w-2.5 -mt-px mr-1" />{a.read_minutes} min read</>
            ) : null}
          </span>
          <span aria-hidden className="h-[30px] w-[30px] shrink-0 rounded-[7px] border border-[#1c2436] bg-[#030407] flex items-center justify-center text-[#f5f7ff] transition-colors group-hover:border-[#c24dff]/50">
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        </span>
      </span>
    </Link>
  );
};

export const ExplainersRail = ({ items }: { items: Article[] }) => {
  const toArticle = useArticlePath();
  if (!items.length) return null;
  return (
    <div data-testid="research-explainers" className={`${SURF} rounded-[9px] p-3.5 sm:p-4 mt-3`}>
      <div className="flex items-center gap-2">
        <h3 className="nl-sg font-medium text-[14px] tracking-[0.02em] text-[#f5f7ff]">EXPLAINERS &amp; GUIDES</h3>
        <InfoTip label="About explainers">
          Plain-language guides from the NewsLayer desk — fundamentals, mechanics and market structure.
        </InfoTip>
        <Link to="/explainers" data-testid="research-explainers-viewall" onClick={() => homeTrack("research", "guides-viewall")}
          className={`ml-auto nl-geist text-[11px] font-medium text-[#c24dff] hover:text-white transition-colors inline-flex items-center gap-1.5 ${FOCUS}`}>
          View all guides <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="mt-3.5 flex items-center gap-3.5">
        <div className="flex-1 flex gap-3.5 overflow-x-auto pb-1 -mb-1 snap-x snap-mandatory lg:grid lg:grid-cols-5 lg:overflow-visible lg:pb-0 lg:mb-0">
          {items.slice(0, 5).map((a, i) => {
            const Icon = catIcon(a);
            const acc = EXPL_ACCENTS[i % 5];
            return (
              <Link key={a.slug} to={toArticle(a.slug)} data-testid={`research-explainer-${i}`}
                onClick={() => homeTrack("research", a.slug)}
                className={`group snap-start shrink-0 w-[240px] lg:w-auto bg-[#020306] border border-[#1c2436] rounded-[7px] p-3 flex items-center gap-3.5 transition-colors hover:border-[#31405f] ${FOCUS}`}>
                <span aria-hidden className="h-[50px] w-[50px] shrink-0 rounded-[7px] bg-[#080512] border flex items-center justify-center"
                  style={{ borderColor: `${acc}73` }}>
                  <Icon className="h-5 w-5" style={{ color: acc }} />
                </span>
                <span className="min-w-0">
                  <span className="nl-geist text-[13px] font-medium leading-[1.3] text-[#f5f7ff] line-clamp-2 group-hover:text-white transition-colors">
                    <TxTitle slug={a.slug} title={a.title} />
                  </span>
                  {a.read_minutes ? (
                    <span className="block nl-mono text-[11px] text-[#a1abbd] mt-1.5">{a.read_minutes} min read</span>
                  ) : null}
                </span>
              </Link>
            );
          })}
        </div>
        <Link to="/explainers" aria-label="More guides" data-testid="research-explainers-next"
          className={`hidden lg:flex h-[30px] w-[30px] shrink-0 rounded-full border border-[#1c2436] bg-[#020306] items-center justify-center text-[#f5f7ff] hover:border-[#c24dff]/50 transition-colors ${FOCUS}`}>
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

export const ResearchSpotlightAd = ({ ad }: { ad: any }) => (
  <aside data-testid="research-spotlight-ad"
    className="bg-[#020306] border border-[#F59E0B]/45 rounded-[10px] overflow-hidden flex flex-col transition-colors hover:border-[#F59E0B]/80">
    <AdLink ad={ad} className="group flex flex-col flex-1" testid="research-spotlight-ad-link">
      <span className="relative block flex-1 min-h-[190px] overflow-hidden">
        <AdMedia ad={ad} className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 group-hover:scale-[1.02]" />
        <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#020306]/70 via-transparent to-transparent" />
        <span className="absolute top-3.5 left-4 inline-flex items-center rounded-md border border-[#F59E0B]/70 bg-[#030407]/90 px-2.5 py-1 nl-mono text-[11px] font-medium text-[#FBBF24]">
          SPONSORED
        </span>
      </span>
      <span className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#1c2436] shrink-0">
        <span className="min-w-0">
          <span className="block nl-sg font-medium text-[15px] leading-[1.3] text-[#f5f7ff] line-clamp-1 group-hover:text-white transition-colors">{ad.name}</span>
          <span className="block nl-mono text-[10px] uppercase tracking-wide text-[#a1abbd] truncate mt-0.5">{ad.advertiser}</span>
        </span>
        <span className="nl-mono text-[9px] uppercase tracking-widest text-[#a1abbd] border border-white/15 rounded px-1.5 py-0.5 shrink-0">Ad</span>
      </span>
    </AdLink>
  </aside>
);

const ResearchAdPromo = () => (
  <aside data-testid="research-ad-promo"
    className="rounded-[10px] border border-dashed border-[#F59E0B]/40 bg-[#020306] flex flex-col items-center justify-center text-center p-6 min-h-[260px]">
    <span className="inline-flex rounded-md border border-[#F59E0B]/60 bg-[#030407] px-2.5 py-1 nl-mono text-[11px] font-medium text-[#FBBF24]">
      AD SPACE AVAILABLE
    </span>
    <p className="nl-sg font-medium text-[19px] leading-[1.3] text-[#f5f7ff] mt-4 max-w-[300px]">
      Put your brand beside our most-read research
    </p>
    <p className="nl-geist text-[12px] leading-[1.4] text-[#a1abbd] mt-2 max-w-[320px]">
      The Research Spotlight is a premium homepage placement seen by every visitor who scrolls the Research &amp; Deep Dives section.
    </p>
    <Link to="/advertise" data-testid="research-ad-promo-cta" onClick={() => homeTrack("research", "ad-promo")}
      className={`mt-5 inline-flex items-center gap-2 rounded-[7px] border border-[#F59E0B]/70 bg-[#241c08] px-4 h-[34px] nl-geist text-[12px] font-medium text-[#f5f7ff] transition-colors hover:bg-[#33270c] ${FOCUS}`}>
      View placements &amp; pricing <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </aside>
);

export const ResearchSection = ({ research }: {
  research?: ResearchPayload & { items?: Article[]; explainer?: Article | null };
}) => {
  const { ad: spotlightAd, hidden: adsHidden } = useAdRotation("research_spotlight");
  const cards = research?.cards || [];
  const pulseItems = research?.pulse?.items || [];
  const explainers = research?.explainers || [];
  if (!research || (!research.featured && !cards.length && !pulseItems.length)) return null;
  const rightRail = !adsHidden && spotlightAd
    ? <ResearchSpotlightAd ad={spotlightAd} />
    : pulseItems.length > 0
      ? <ResearchPulse pulse={research.pulse!} />
      : !adsHidden ? <ResearchAdPromo /> : null;
  return (
    <TooltipProvider delayDuration={150}>
    <section data-testid="research-section" className="mt-10 sm:mt-14">
      <SectionHead title="Research & Deep Dives"
        sub="Expert analysis, market intelligence, and deep research to navigate tomorrow."
        cta="View all research" to="/research" testid="research-viewall" module="research" />
      {(research.featured || rightRail) && (
        <div className="grid grid-cols-1 lg:grid-cols-[914fr_466fr] gap-3">
          {research.featured && <FeaturedAnalysis a={research.featured} />}
          {rightRail}
        </div>
      )}
      {cards.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-6">
          {cards.slice(0, 4).map((a, i) => <ResearchCard key={a.slug} a={a} idx={i} />)}
        </div>
      )}
      <ExplainersRail items={explainers} />
      <p data-testid="research-footer-note" className="nl-geist text-[11px] text-[#a1abbd] text-center mt-6">
        <span aria-hidden className="text-[#f5f7ff] mr-1.5">✦</span>
        New research published daily<span aria-hidden className="mx-2.5 text-[#4a5163]">•</span>Follow topics and authors to personalize your feed
      </p>
    </section>
    </TooltipProvider>
  );
};
